import { parseDepositAmount } from "../../../../../utils/number";
import { PaymentsAutoCloseConfiguration } from "../../../../../types/VenueGroup";
import { BookingV4 } from "../../../../../types/bookings";

type DepositLike = {
  type: string;
  status: string;
  amount: number | { amount: number };
  claims?: Array<{ amount: number | { amount: number } }>;
  refunds?: string[] | null;
  refund_ids?: string[] | null;
  dispute?: boolean;
};

export const canClaimDeposit = (deposit: DepositLike): boolean => {
  const claimableTypes = ["request_auth", "manual_auth", "card_authorised"];

  if (!claimableTypes.includes(deposit.type)) {
    return false;
  }

  if (deposit.status !== "paid") {
    return false;
  }

  if (deposit.claims && deposit.claims.length > 0) {
    return false;
  }

  return true;
};

export const canRefundDeposit = (
  deposit: DepositLike,
  booking: BookingV4
): boolean => {
  if (deposit.status !== "paid") {
    return false;
  }

  if (deposit.dispute) {
    return false;
  }

  const totalClaimed =
    deposit.claims?.reduce(
      (sum, claim) => sum + parseDepositAmount(claim.amount),
      0
    ) ?? 0;

  const refundRefs = deposit.refunds || deposit.refund_ids || [];
  const totalRefunded = refundRefs.reduce((sum, refundRef) => {
    const refund = booking.refunds?.find(
      (r) => r.id === refundRef || r.refund_ref === refundRef
    );
    if (refund) {
      return sum + parseDepositAmount(refund.amount);
    }
    return sum;
  }, 0);

  const remainingRefundable = totalClaimed - totalRefunded;

  return remainingRefundable > 0;
};

export const getMaxClaimAmount = (deposit: DepositLike): number => {
  if (deposit.claims && deposit.claims.length > 0) {
    return 0;
  }

  return parseDepositAmount(deposit.amount);
};

export const isPaymentWindowExpired = (
  bookingStartTime: string | Date,
  autoCloseConfig: PaymentsAutoCloseConfiguration | undefined
): boolean => {
  if (!autoCloseConfig || !autoCloseConfig.enabled || !bookingStartTime) {
    return false;
  }

  const isCustom = autoCloseConfig.type === "custom";
  const unit = isCustom ? autoCloseConfig.unit : "minute";
  const value = isCustom ? autoCloseConfig.value : 0;
  const isBefore = autoCloseConfig.tense === "before";

  const bookingDate = new Date(bookingStartTime);
  const now = new Date();
  const expirationDate = new Date(bookingDate);

  switch (unit) {
    case "hour":
      expirationDate.setHours(
        expirationDate.getHours() + (isBefore ? -value : value)
      );
      break;
    case "day":
      expirationDate.setDate(
        expirationDate.getDate() + (isBefore ? -value : value)
      );
      break;
    case "week":
      expirationDate.setDate(
        expirationDate.getDate() + (isBefore ? -value : value) * 7
      );
      break;
    case "minute":
    default:
      expirationDate.setMinutes(
        expirationDate.getMinutes() + (isBefore ? -value : value)
      );
  }

  return now > expirationDate;
};
